/**
 * AULA INVERTIDA (M5) — vista del PLAN DE CLASE del alumno.
 *
 * La recompensa pedagógica: para una `class_session` concreta muestra la ruta
 *   ANTES  → mira este vídeo + lee este recurso
 *   DURANTE → asiste a la clase (botón "Entrar" cuando está en vivo)
 *   DESPUÉS → entrega el desafío
 * con un tick de completitud por ítem, reutilizando el progreso que ya existe
 * (lesson_progress, assignment_submission, video_watch_progress). Local-first:
 * session_module y el progreso llegan por los streams de cohorte → offline.
 */
import { type Component, createMemo, For, type JSX, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { useNavigate, useParams } from "@tanstack/solid-router";
import {
  BookOpen,
  CalendarClock,
  CheckCircle2,
  Circle,
  ClipboardList,
  FileText,
  GraduationCap,
  MessagesSquare,
  Radio,
  Video,
} from "lucide-solid";
import {
  directoryRowTitle,
  groupPlanByPhase,
  isPlanPhase,
  itemCompletion,
  joinWindowState,
  type PlanCompletionCtx,
  type PlanModule,
  type PlanPhase,
  planProgress,
} from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { EnVivoBadge } from "@/features/live/components/en-vivo-badge";
import { RecordingPlayer } from "@/features/live/components/recording-player";
import { useNow } from "@/features/live/hooks/use-now";
import { useCohort } from "@/features/live/hooks/use-live-data";
import {
  useCourseModuleDirectory,
  useMyCourseProgress,
  useMySubmissions,
  useMyWatchProgress,
  useSessionPlanItems,
  useSessionRow,
} from "../hooks/use-session-plan";

/** Rótulo + icono de cada fase (el orden pedagógico lo da PLAN_PHASES). */
const PHASE_META: Record<
  PlanPhase,
  { title: MessageDescriptor; hint: MessageDescriptor; icon: Component<{ class?: string }> }
> = {
  pre: {
    title: msg`Before class`,
    hint: msg`Get ready: watch and read before you arrive.`,
    icon: BookOpen,
  },
  live: {
    title: msg`During class`,
    hint: msg`Join the live session and take part.`,
    icon: Radio,
  },
  post: {
    title: msg`After class`,
    hint: msg`Consolidate: hand in your work.`,
    icon: ClipboardList,
  },
};

function kindIcon(kind: string | null): Component<{ class?: string }> {
  switch (kind) {
    case "lesson": {
      return GraduationCap;
    }
    case "resource": {
      return FileText;
    }
    case "video": {
      return Video;
    }
    case "assignment": {
      return ClipboardList;
    }
    case "forum": {
      return MessagesSquare;
    }
    case "live_session": {
      return Radio;
    }
    default: {
      return FileText;
    }
  }
}

/** Atributo data-done para el e2e (done|pending|informativo). */
function doneAttr(completion: "done" | "pending" | null): string {
  if (completion === "done") {
    return "true";
  }
  if (completion === "pending") {
    return "false";
  }
  return "";
}

/** Nota de encuadre por ítem (session_module.config.note). */
function parseNote(config: string | null | undefined): string | null {
  if (!config) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(config);
    if (parsed && typeof parsed === "object" && "note" in parsed) {
      const { note } = parsed as { note?: unknown };
      return typeof note === "string" && note.trim() !== "" ? note : null;
    }
  } catch {
    return null;
  }
  return null;
}

export const SessionPlanView: Component<{ sessionId: string }> = (props) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const routeParams = useParams({ from: "/u/$u" });
  const now = useNow();

  const sessionRow = useSessionRow(() => props.sessionId);
  const session = () => sessionRow.data?.[0];
  const cohortId = () => session()?.cohort_id ?? undefined;
  const cohort = useCohort(cohortId);
  const courseId = () => cohort.data?.[0]?.course_id ?? undefined;

  const planItems = useSessionPlanItems(() => props.sessionId);
  const directory = useCourseModuleDirectory(courseId);
  const progress = useMyCourseProgress(courseId);
  const submissions = useMySubmissions();
  const watch = useMyWatchProgress();

  const dirMap = createMemo(() => {
    const map = new Map<
      string,
      { kind: string | null; ref_id: string | null; title: string | null }
    >();
    for (const r of directory.data ?? []) {
      map.set(r.id, { kind: r.kind, ref_id: r.ref_id, title: directoryRowTitle(r) });
    }
    return map;
  });

  const planModules = createMemo<PlanModule[]>(() => {
    const dir = dirMap();
    return (planItems.data ?? []).map((sm) => {
      const d = dir.get(sm.module_id ?? "");
      return {
        id: sm.id,
        moduleId: sm.module_id ?? "",
        phase: isPlanPhase(sm.phase) ? sm.phase : "pre",
        position: sm.position ?? 0,
        kind: d?.kind ?? null,
        refId: d?.ref_id ?? null,
        title: d?.title ?? null,
        note: parseNote(sm.config),
      } satisfies PlanModule;
    });
  });

  const ctx = createMemo<PlanCompletionCtx>(() => {
    const lessonStatusByRef = new Map<string, string | null>();
    for (const p of progress.data ?? []) {
      if (p.lesson_id) {
        lessonStatusByRef.set(p.lesson_id, p.status);
      }
    }
    const submittedByAssignment = new Map<string, boolean>();
    for (const s of submissions.data ?? []) {
      if (s.assignment_id) {
        submittedByAssignment.set(s.assignment_id, true);
      }
    }
    const videoDoneByRef = new Map<string, boolean>();
    for (const w of watch.data ?? []) {
      if (w.video_id) {
        videoDoneByRef.set(w.video_id, Boolean(w.completed_at));
      }
    }
    return { lessonStatusByRef, submittedByAssignment, videoDoneByRef };
  });

  const grouped = createMemo(() => groupPlanByPhase(planModules()));
  const overall = createMemo(() => planProgress(planModules(), ctx()));
  const hasPlan = () => planModules().length > 0;

  const times = () => ({
    date: session()?.date,
    startTime: session()?.start_time,
    endTime: session()?.end_time,
  });
  const isLive = () => joinWindowState(now(), times()).isLiveNow;

  const enterLive = () => {
    void navigate({
      to: "/u/$u/live/$sessionId",
      params: { u: routeParams().u, sessionId: props.sessionId },
    });
  };

  const openModule = () => {
    const cid = courseId();
    if (cid) {
      void navigate({ to: "/u/$u/learn/$courseId", params: { u: routeParams().u, courseId: cid } });
    }
  };

  const renderItem = (m: PlanModule): JSX.Element => {
    const completion = itemCompletion(m, ctx());
    const Icon = kindIcon(m.kind);
    return (
      <li
        class="flex items-start gap-3 rounded-lg border border-border px-3 py-2"
        data-testid="plan-item"
        data-kind={m.kind ?? ""}
        data-phase={m.phase}
        data-done={doneAttr(completion)}
      >
        <span class="mt-0.5 shrink-0" data-testid="plan-item-tick">
          <Show
            when={completion !== null}
            fallback={<Icon class="h-4 w-4 text-muted-foreground" />}
          >
            <Show
              when={completion === "done"}
              fallback={<Circle class="h-4 w-4 text-muted-foreground" />}
            >
              <CheckCircle2 class="h-4 w-4 text-success" />
            </Show>
          </Show>
        </span>
        <button
          type="button"
          class="flex-1 text-left"
          data-testid="plan-item-open"
          onClick={openModule}
        >
          <span class="flex items-center gap-2 text-sm font-semibold">
            <Icon class="h-3.5 w-3.5 text-muted-foreground" />
            {m.title ?? t(msg`Module`)}
          </span>
          <Show when={m.note}>
            <span class="mt-0.5 block text-xs text-muted-foreground">{m.note}</span>
          </Show>
        </button>
      </li>
    );
  };

  return (
    <div class="mx-auto max-w-3xl space-y-5 p-4 md:p-6" data-testid="session-plan-view">
      {/* Cabecera de la clase */}
      <div class="flex items-start justify-between gap-2">
        <div>
          <h1 class="flex items-center gap-2 font-display text-2xl font-extrabold">
            <CalendarClock class="h-6 w-6 text-primary" />
            {session()?.topic ?? t(msg`Class plan`)}
          </h1>
          <p class="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
            <Show when={session()}>
              {session()?.date} · {(session()?.start_time ?? "").slice(0, 5)}–
              {(session()?.end_time ?? "").slice(0, 5)}
            </Show>
            <EnVivoBadge times={times()} />
          </p>
        </div>
      </div>

      {/* Progreso global del plan */}
      <Show when={hasPlan() && overall().total > 0}>
        <div
          class="rounded-xl border border-border bg-muted/20 px-4 py-3 text-sm"
          data-testid="plan-progress"
          data-done={overall().done}
          data-total={overall().total}
        >
          <span class="font-semibold">{t(msg`Your progress`)}:</span> {overall().done}/
          {overall().total} {t(msg`tasks done`)}
        </div>
      </Show>

      <Show
        when={hasPlan()}
        fallback={
          <p class="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            {t(msg`No class plan yet for this session.`)}
          </p>
        }
      >
        <For each={["pre", "live", "post"] as PlanPhase[]}>
          {(phase) => {
            const items = () => grouped()[phase];
            const meta = PHASE_META[phase];
            const PhaseIcon = meta.icon;
            return (
              <section
                class="space-y-2 rounded-2xl border border-border p-4"
                data-testid={`plan-phase-${phase}`}
              >
                <div class="flex items-center gap-2">
                  <PhaseIcon class="h-5 w-5 text-accent" />
                  <h2 class="font-display text-lg font-bold">{t(meta.title)}</h2>
                </div>
                <p class="text-xs text-muted-foreground">{t(meta.hint)}</p>

                {/* La fase DURANTE ancla la clase en vivo: badge + entrar + grabación */}
                <Show when={phase === "live"}>
                  <div
                    class="flex flex-wrap items-center gap-2 rounded-lg bg-primary/5 px-3 py-2"
                    data-testid="plan-live-anchor"
                  >
                    <span class="text-sm font-semibold">{t(msg`Live class`)}</span>
                    <Show when={isLive()}>
                      <button
                        type="button"
                        class="inline-flex items-center gap-1 rounded-lg bg-primary px-2.5 py-1 text-xs font-bold text-primary-foreground"
                        data-testid="plan-enter-live"
                        onClick={enterLive}
                      >
                        <Video class="h-3.5 w-3.5" /> {t(msg`Enter`)}
                      </button>
                    </Show>
                    <Show when={session()?.recording_video_id}>
                      <RecordingPlayer
                        videoId={session()!.recording_video_id!}
                        title={session()?.topic ?? undefined}
                      />
                    </Show>
                  </div>
                </Show>

                <Show
                  when={items().length > 0}
                  fallback={
                    <p class="text-xs text-muted-foreground">{t(msg`Nothing for this phase.`)}</p>
                  }
                >
                  <ul class="space-y-1.5">
                    <For each={items()}>{(m) => renderItem(m)}</For>
                  </ul>
                </Show>
              </section>
            );
          }}
        </For>
      </Show>
    </div>
  );
};
